import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import ReactJson from 'react18-json-view';
import { css } from '/imports/ui/css';
import { strings } from '/imports/strings';
import { localStorageGet, localStorageSet } from '/imports/utils';
import { toastOnError } from '/imports/ui/utils';
import { Button } from '/imports/ui/Button';
import { DevToolSection } from '/imports/ui/DevToolSection';
import { FormField } from '/imports/ui/FormField';
import { Input } from '/imports/ui/Input';
import { Select } from '/imports/ui/Select';
import { TextArea } from '/imports/ui/TextArea';
import {
  QuickbooksApiCallResult, quickbooksApiCall, quickbooksEndpoints, quickbooksStatus,
} from '/imports/api/quickbooks';

const DevToolQuickbooksApiStyled = styled.div`
  /* FormField carries no layout of its own — it leans on the ambient form css of whatever page hosts it,
     and the dev tools page has none, so the labels sit inline against the inputs without this. */
  .field { margin-bottom: 0.75rem; max-width: 46rem; }
  label { display: block; margin-bottom: 0.25rem; font-weight: 500; }
  .field .input, .field input, .field select, .field textarea { width: 100%; box-sizing: border-box; }
  textarea { min-height: 4rem; font-family: monospace; font-size: 0.8rem; }
  .send-row { margin: 0.5rem 0 0.75rem; }
  .meta-row { color: ${css.grey3}; font-size: 0.8rem; margin-top: 0.25rem; }
  .result-head { display: flex; gap: 1rem; flex-wrap: wrap; align-items: baseline; margin-top: 0.75rem; }
  .result-head .status { font-weight: 600; }
  .result-head .status.ok { color: ${css.green}; }
  .result-head .status.failed { color: ${css.red2}; }
  .result-url { word-break: break-all; font-family: monospace; font-size: 0.75rem; color: ${css.grey3}; }
  /* The body of a report or a 20-row query is long; let it scroll here rather than push the rest of the
     dev tools page down by several screens. */
  .response-body {
    max-height: 28rem; overflow: auto; background: ${css.grey9};
    padding: 0.5rem; border-radius: ${css.borderRadius};
  }
  .response-body pre { margin: 0; }
`;

const endpointOptions = quickbooksEndpoints.map((e) => ({ value: e.key, label: e.label }));
const environmentOptions = [
  { value: 'sandbox', label: strings.Sandbox() },
  { value: 'production', label: strings.Production() },
];

// The whole form under one key, so a half-finished query survives a reload. That includes the access
// token: it is a bearer credential at rest in the browser, which is the trade for not re-pasting it
// every time — clearing the field forgets it, and the field is the only place it lives client-side.
const STORAGE_KEY = 'quickbooks.explorer';

interface ExplorerForm {
  endpointKey: string;
  path: string;
  params: string;
  accessToken: string;
  realmId: string;
  environment: 'sandbox' | 'production';
  minorVersion: string;
}

const defaultForm = (): ExplorerForm => ({
  endpointKey: quickbooksEndpoints[0].key,
  path: quickbooksEndpoints[0].path,
  params: quickbooksEndpoints[0].params || '',
  accessToken: '',
  realmId: '',
  environment: 'sandbox',
  minorVersion: '',
});

/** Dev tool: send one GET to the Intuit Accounting API and show the raw response. */
export const DevToolQuickbooksApi = () => {
  const [saved] = useState(() => {
    try {
      const value = typeof localStorage === 'undefined' ? null : localStorageGet(STORAGE_KEY);
      return value && typeof value === 'object' ? value as Partial<ExplorerForm> : null;
    }
    catch (_) {
      // Private browsing refuses localStorage outright; the tool works fine without it.
      return null;
    }
  });
  // Spread over the defaults rather than trusting what was read: a key added to the form later would
  // otherwise come back undefined for anyone with an older blob still in their browser.
  const [form, setForm] = useState<ExplorerForm>(() => ({ ...defaultForm(), ...saved }));
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<QuickbooksApiCallResult>();
  const { endpointKey, path, params, accessToken, realmId, environment, minorVersion } = form;
  /**
   * Applies a change to the form and remembers it.
   * @param patch - the fields that changed
   */
  const set = (patch: Partial<ExplorerForm>) => setForm((current) => {
    const next = { ...current, ...patch };
    try { localStorageSet(STORAGE_KEY, next); }
    catch (_) { /* nothing to do — the form still works, it just will not survive a reload */ }
    return next;
  });
  // Prefill from whatever is already connected, so the common case — poke at the org's own company — is
  // a two-click operation rather than a hunt for the realm id. Skipped once there is something
  // remembered, since overwriting a realm the user deliberately typed would be worse than not helping.
  useEffect(() => {
    if (saved) return;
    quickbooksStatus.call(undefined).then((status) => {
      set({
        ...(status.realmId ? { realmId: status.realmId } : {}),
        ...(status.environment ? { environment: status.environment } : {}),
      });
    }).catch(() => undefined);
  }, []);
  /**
   * Loads a preset into the path/params fields.
   * @param key - the chosen endpoint's key
   */
  const selectEndpoint = (key: string) => {
    const endpoint = quickbooksEndpoints.find((e) => e.key === key);
    if (!endpoint) return;
    set({ endpointKey: key, path: endpoint.path, params: endpoint.params || '' });
  };
  const send = toastOnError(async () => {
    if (loading) return;
    setLoading(true);
    setResult(undefined);
    try {
      setResult(await quickbooksApiCall.call({
        path,
        params: params || undefined,
        accessToken: accessToken || undefined,
        realmId: realmId || undefined,
        environment,
        minorVersion: minorVersion || undefined,
      }));
    }
    finally {
      setLoading(false);
    }
  });
  return (
    <DevToolSection
      title={strings.QuickbooksApiExplorer()}
      description={strings.QuickbooksApiExplorerHelp()}
    >
      <DevToolQuickbooksApiStyled>
        <FormField field="endpoint">
          <label>{strings.Endpoint()}</label>
          <Select value={endpointKey} options={endpointOptions} onChange={(v) => selectEndpoint(v)} />
        </FormField>
        <FormField field="path">
          <label>{strings.Path()}</label>
          <Input value={path} onChange={(v) => set({ path: v || '' })} />
        </FormField>
        <FormField field="params">
          <label>{strings.Parameters()}</label>
          <TextArea value={params} onChange={(v) => set({ params: v || '' })} />
          <div className="meta-row">{strings.QuickbooksApiParamsHint()}</div>
        </FormField>
        <FormField field="accessToken">
          <label>{strings.AccessToken()}</label>
          <Input value={accessToken} type="password" onChange={(v) => set({ accessToken: v || '' })} />
          <div className="meta-row">{strings.QuickbooksApiTokenHint()}</div>
        </FormField>
        <FormField field="realmId">
          <label>{strings.CompanyId()}</label>
          <Input value={realmId} onChange={(v) => set({ realmId: v || '' })} />
          <div className="meta-row">{strings.QuickbooksApiCompanyHint()}</div>
        </FormField>
        <FormField field="environment">
          <label>{strings.Environment()}</label>
          <Select value={environment} options={environmentOptions} onChange={(v) => set({ environment: v as 'sandbox' | 'production' })} />
        </FormField>
        <FormField field="minorVersion">
          <label>{strings.MinorVersion()}</label>
          <Input value={minorVersion} onChange={(v) => set({ minorVersion: v || '' })} />
        </FormField>
        <div className="send-row">
          <Button onClick={() => send()} disabled={loading}>{loading ? strings.Sending() : strings.Send()}</Button>
        </div>
        {
          result ?
            <div>
              <div className="result-head">
                <span className={`status ${result.ok ? 'ok' : 'failed'}`}>{strings.Status()}: {result.status}</span>
                <span>{result.durationMs}ms</span>
                <span>{result.environment} · {result.realmId}</span>
                {/* Intuit support cannot look a call up without its trace id, so it is shown for a call
                    that succeeded as much as for one that did not. */}
                {result.tid ? <span>intuit_tid: {result.tid}</span> : null}
              </div>
              <div className="result-url">{result.url}</div>
              {result.message ? <div className="meta-row">{result.message}</div> : null}
              <label>{strings.Response()}</label>
              <div className="response-body">
                {
                  // Intuit answers with JSON until something upstream of it does not — a gateway
                  // timeout comes back as html, and a tree view of a string is no view at all.
                  result.body && typeof result.body === 'object' ?
                    <ReactJson src={result.body} collapsed={2} displaySize />
                    :
                    <pre>{`${result.body ?? ''}`}</pre>
                }
              </div>
            </div>
            :
            null
        }
      </DevToolQuickbooksApiStyled>
    </DevToolSection>
  );
};
